import { useMemo } from "react";
import { useAuthenticated } from "../auth";
import { portalServer } from "../config";
import i18n from "../i18n";
import { usePortalApi } from "./portal-api";
import {
  type SyncModule,
  useSyncData,
  useSyncModuleRegistration,
} from "../sync";
import type { KartenObjekt, KartenObjektDatei } from "../types";
import { limiter } from "./demo";
import { daily } from "./provider";
import { precacheResources } from "./resource";

const syncModuleId = "hydranten";
const precacheSyncModuleId = "hydranten:precache";

function useHydrantenSyncModule(): SyncModule<ReadonlyArray<KartenObjekt>> {
  const { portalKey, demo } = useAuthenticated();
  const { useNewApi, baseUrl } = usePortalApi();

  return useMemo(
    () => ({
      id: syncModuleId,
      i18nKey: `karte:tasks.${syncModuleId}`,
      defaultOptions: { schedule: { type: "periodic", interval: daily } },
      fetchData: useNewApi && baseUrl
        ? () => fetchHydrantenNew(portalKey, baseUrl)
        : async () => {
            const response = await fetch(
              `${portalServer}/get-hydrants.php?ORG_ACCESSKEY=${encodeURIComponent(
                portalKey,
              )}`,
            );

            if (!response.ok) {
              throw new Error(
                `Error fetching data: ${response.status} ${response.statusText}`,
              );
            }

            const data = await response.json();
            const limited = limiter<KartenObjekt>(demo, 300);
            return limited(data.HYDRANT_LIST ?? []).map(parseHydrantLegacy);
          },
    }),
    [demo, portalKey, useNewApi, baseUrl],
  );
}

async function fetchHydrantenNew(
  portalKey: string,
  baseUrl: string,
): Promise<ReadonlyArray<KartenObjekt>> {
  const response = await fetch(`${baseUrl}/api/device/sync/water-supply`, {
    headers: { Authorization: `Bearer ${portalKey}` },
  });

  if (!response.ok) {
    throw new Error(
      `Error fetching data: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  return (data.waterSupply ?? []).map((entry: any) =>
    parseHydrantNew(entry, portalKey, baseUrl),
  );
}

function parseHydrantNew(
  entry: any,
  portalKey: string,
  baseUrl: string,
): KartenObjekt {
  const { typ } = waterSupplyTypeMap[entry.type as WaterSupplyType] ??
    waterSupplyTypeMap.SONSTIGES;

  const beschreibungParts: string[] = [];
  if (entry.volume) beschreibungParts.push(`${entry.volume} l/min`);
  if (entry.status && entry.status !== "UNBEKANNT")
    beschreibungParts.push(
      i18n.t(`karte:water-supply-status.${entry.status}`, {
        defaultValue: entry.status,
      }),
    );
  if (entry.access) beschreibungParts.push(entry.access);
  if (entry.remarks) beschreibungParts.push(entry.remarks);

  return {
    id: entry.id,
    typ,
    icon: parseHydrantIconNew(entry.customIcon),
    latitude: entry.latitude,
    longitude: entry.longitude,
    name: entry.name,
    adresse: entry.address ?? undefined,
    beschreibung: beschreibungParts.length > 0
      ? beschreibungParts.join(" · ")
      : undefined,
    dateien: parseHydrantDateienNew(entry.images, portalKey, baseUrl),
  };
}

function parseHydrantDateienNew(
  images: any[],
  portalKey: string,
  baseUrl: string,
): ReadonlyArray<KartenObjektDatei> | undefined {
  if (!images?.length) return undefined;

  const dateien = images
    .toSorted((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
    .map((image: any) => ({
      id: image.id,
      name: image.originalName || image.filename,
      mimeType: image.mimeType,
      url: `${baseUrl}/api/device/sync/water-supply/images/${image.id}/download`,
      fetchOptions: { headers: { Authorization: `Bearer ${portalKey}` } },
    }));

  return dateien.length > 0 ? dateien : undefined;
}

// New API: entries can carry an arbitrary custom icon independent of `type`
// (e.g. "icon_we_h_we.png"), already matching a @rescuetablet/map-icons key
// once the extension is stripped. Falls back to `typ` (set from `type`
// above) when absent — same as the legacy hydrants below, which never set
// an explicit icon.
function parseHydrantIconNew(customIcon?: string | null): string | undefined {
  if (!customIcon) return undefined;
  return customIcon.replace(/\.[^.]+$/, "");
}

export function useHydrantenSync() {
  const module = useHydrantenSyncModule();
  useSyncModuleRegistration(module);
}

function useHydrantenPrecacheSyncModule(): SyncModule<void> {
  return useMemo(
    () => ({
      id: precacheSyncModuleId,
      i18nKey: `karte:tasks.${syncModuleId}.precache`,
      defaultOptions: {
        schedule: { type: "daily", hour: 3, minute: 0 },
        requiredNetworkStatus: ["wifi"],
      },
      dependsOn: [syncModuleId],
      fetchData: async ({ dataProvider, observer }) => {
        const hydranten =
          dataProvider.getModuleData<ReadonlyArray<KartenObjekt>>(
            syncModuleId,
          ) ?? [];
        const dateien = hydranten.flatMap((h) => h.dateien ?? []);
        if (dateien.length === 0) return;

        await precacheResources(
          dateien.map((d) => d.url),
          { requestOptions: dateien[0].fetchOptions },
          observer,
        );
      },
    }),
    [],
  );
}

export function useHydrantenPrecacheSync() {
  const module = useHydrantenPrecacheSyncModule();
  useSyncModuleRegistration(module);
}

export function useHydranten(): ReadonlyArray<KartenObjekt> {
  return useSyncData(syncModuleId) ?? [];
}

function parseHydrantLegacy(entry: any): KartenObjekt {
  return {
    id: entry.HYDRANT_ID,
    latitude: parseFloat(entry.HYDRANT_GEO_LAT),
    longitude: parseFloat(entry.HYDRANT_GEO_LNG),
    beschreibung: entry.HYDRANT_DESCRIPTION,
    ...(hydrantTypenLegacy[entry.HYDRANT_TYPE] || { typ: "hydrant", name: "" }),
  };
}

type WaterSupplyType =
  | "LOESCHWASSERBEHAELTER"
  | "LOESCHWASSERBRUNNEN"
  | "LOESCHWASSERTEICH"
  | "OFFENES_GEWAESSER"
  | "SAUGSTELLE"
  | "SONSTIGES"
  | "UEBERFLURHYDRANT"
  | "UNTERFLURHYDRANT"
  | "WASSERENTNAHMESTELLE"
  | "WUERTTEMBERGER_SCHACHTHYDRANT"
  | "ZISTERNE";

const waterSupplyTypeMap: Record<WaterSupplyType, { typ: string }> = {
  UNTERFLURHYDRANT:             { typ: "hydrant" },
  WUERTTEMBERGER_SCHACHTHYDRANT: { typ: "hydrant" },
  UEBERFLURHYDRANT:             { typ: "hydrant_o" },
  LOESCHWASSERBEHAELTER:        { typ: "hydrant" },
  LOESCHWASSERBRUNNEN:          { typ: "hydrant" },
  LOESCHWASSERTEICH:            { typ: "hydrant" },
  OFFENES_GEWAESSER:            { typ: "hydrant" },
  SAUGSTELLE:                   { typ: "hydrant" },
  WASSERENTNAHMESTELLE:         { typ: "hydrant" },
  ZISTERNE:                     { typ: "hydrant" },
  SONSTIGES:                    { typ: "hydrant" },
};

const hydrantTypenLegacy: { [typ: string]: { typ: string; name: string } } = {
  1:   { typ: "hydrant",   name: "Unterflurhydrant (H80)" },
  2:   { typ: "hydrant",   name: "Unterflurhydrant (H100)" },
  7:   { typ: "hydrant",   name: "Unterflurhydrant (H125)" },
  3:   { typ: "hydrant",   name: "Unterflurhydrant (H150)" },
  8:   { typ: "hydrant",   name: "Unterflurhydrant (H200)" },
  16:  { typ: "hydrant",   name: "Unterflurhydrant (H250)" },
  9:   { typ: "hydrant",   name: "Unterflurhydrant (H300)" },
  18:  { typ: "hydrant",   name: "Unterflurhydrant (H350)" },
  10:  { typ: "hydrant",   name: "Unterflurhydrant (H400)" },
  4:   { typ: "hydrant_o", name: "Überflurhydrant (H80)" },
  11:  { typ: "hydrant_o", name: "Überflurhydrant (H100)" },
  12:  { typ: "hydrant_o", name: "Überflurhydrant (H150)" },
  13:  { typ: "hydrant_o", name: "Überflurhydrant (H200)" },
  17:  { typ: "hydrant_o", name: "Überflurhydrant (H250)" },
  14:  { typ: "hydrant_o", name: "Überflurhydrant (H300)" },
  19:  { typ: "hydrant_o", name: "Überflurhydrant (H350)" },
  15:  { typ: "hydrant_o", name: "Überflurhydrant (H400)" },
  5:   { typ: "hydrant",   name: "Offenes Gewässer" },
  6:   { typ: "hydrant",   name: "Zisterne / Sonstiges" },
  666: { typ: "hydrant",   name: "Mess-, Regel- oder Spülhydrant (nicht für Feuerwehr)" },
};
